import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Lightbulb, Sparkles, Plus, Loader2, Trash2, Rocket, Save, Link as LinkIcon,
  FileText, BookOpen, Users, CheckSquare, Square, Layers, ExternalLink, BadgeCheck,
  Radar, Wand2, Check, X, Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";
const DIFF = { low: "text-emerald-300 bg-emerald-500/10", med: "text-amber-300 bg-amber-500/10", high: "text-rose-300 bg-rose-500/10" };

function Card({ children, className = "" }) {
  return <div className={`bg-[#111827] border border-slate-800/80 rounded-xl p-5 ${className}`}>{children}</div>;
}

function IdeasTab({ cid }) {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState(null);
  const [nt, setNt] = useState({ title: "", audience_angle: "", hook: "" });
  const [count, setCount] = useState(3);
  const [est, setEst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const [sel, setSel] = useState([]);

  const load = useCallback(async () => { const { data } = await api.get(`/channels/${cid}/ideas`); setIdeas(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/channels/${cid}/ideas/generate-estimate?count=${count}`).then((r) => setEst(r.data)).catch(() => {}); }, [cid, count]);

  const create = async () => {
    if (!nt.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.post(`/channels/${cid}/ideas`, nt); toast.success("Idea added"); setNt({ title: "", audience_angle: "", hook: "" }); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const generate = async () => {
    setGen(true);
    try { const { data } = await api.post(`/channels/${cid}/ideas/generate`, { count }); toast.success(`${data.count} AI ideas generated`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setGen(false); }
  };
  const setStatus = async (idea, status) => {
    try { await api.put(`/ideas/${idea.id}`, { ...idea, status }); await load(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/ideas/${id}`); await load(); } catch (e) { toast.error(apiError(e)); } };
  const promote = async (id) => {
    try { const { data } = await api.post(`/ideas/${id}/promote`); toast.success("Promoted to project"); navigate(`/projects/${data.project.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 5 ? (toast.error("Max 5 per batch"), s) : [...s, id]));
  const batch = async () => {
    if (sel.length === 0) return;
    try { const { data } = await api.post(`/channels/${cid}/batch-produce`, { idea_ids: sel });
      toast.success(`Batch: ${data.succeeded} created, ${data.failed} failed`); setSel([]); await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!ideas) return <LoadingState />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-semibold text-slate-100">Idea backlog</h3>
          {sel.length > 0 && (
            <Button onClick={batch} size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="batch-produce-btn">
              <Layers className="w-4 h-4 mr-1.5" /> Produce {sel.length} (batch)
            </Button>
          )}
        </div>
        {ideas.length === 0 ? <Card><EmptyState icon={Lightbulb} title="No ideas yet" description="Add one manually or generate with AI." /></Card> : (
          <div className="space-y-3" data-testid="ideas-list">
            {ideas.map((i) => (
              <Card key={i.id} className="border-l-2 border-l-violet-600/40" >
                <div className="flex items-start gap-3">
                  <button onClick={() => toggle(i.id)} className="mt-0.5 text-slate-400 hover:text-violet-300" data-testid={`select-idea-${i.id}`}>
                    {sel.includes(i.id) ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0" data-testid={`idea-card-${i.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-100">{i.title}</span>
                      {i.ai_generated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 inline-flex items-center gap-1" data-testid={`idea-ai-badge-${i.id}`}><Sparkles className="w-3 h-3" /> AI-generated</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${DIFF[i.difficulty] || DIFF.med}`}>{i.difficulty}</span>
                      {i.score != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">score {i.score} (est.)</span>}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{i.status}</span>
                    </div>
                    {i.audience_angle && <p className="text-xs text-slate-400 mt-1">Angle: {i.audience_angle}</p>}
                    {i.hook && <p className="text-xs text-slate-500 mt-0.5">Hook: {i.hook}</p>}
                    {i.score_reasons && <p className="text-xs text-slate-500 mt-0.5 italic">Why (AI estimate): {i.score_reasons}</p>}
                    {i.promoted_project_id && <button onClick={() => navigate(`/projects/${i.promoted_project_id}`)} className="text-xs text-violet-300 underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open project</button>}
                    <div className="flex items-center gap-2 mt-2">
                      <Select value={i.status} onValueChange={(v) => setStatus(i, v)}>
                        <SelectTrigger className="h-7 w-32 bg-slate-900 border-slate-800 text-slate-200 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                          {["backlog", "considering", "promoted", "rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!i.promoted_project_id && <Button onClick={() => promote(i.id)} size="sm" className="h-7 bg-violet-600 hover:bg-violet-500 text-white text-xs" data-testid={`promote-idea-${i.id}`}><Rocket className="w-3.5 h-3.5 mr-1" /> Promote</Button>}
                      <button onClick={() => del(i.id)} className="text-slate-500 hover:text-rose-400 ml-auto"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <RadarPanel cid={cid} onRan={load} />
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-300" /> Generate with AI</h3>
          <p className="text-xs text-slate-500 mb-3">Grounded in this channel's profile + playbook. Ideas are AI estimates, not observed metrics.</p>
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-slate-300 text-sm">Count</Label>
            <Input type="number" min={1} max={10} value={count} onChange={(e) => setCount(Math.max(1, Math.min(10, Number(e.target.value))))} className={`${inputCls} w-20 h-8`} data-testid="gen-count" />
          </div>
          {est && <p className="text-xs text-slate-500 mb-3" data-testid="gen-estimate">Est. cost: {est.estimate == null ? "unknown" : `$${est.estimate}`} · {est.model}</p>}
          <Button onClick={generate} disabled={gen} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="generate-ideas-btn">
            {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate {count} ideas</>}
          </Button>
        </Card>
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Add manually</h3>
          <div className="space-y-2">
            <Input className={inputCls} placeholder="Proposed title" value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} data-testid="idea-create-title" />
            <Input className={inputCls} placeholder="Audience angle" value={nt.audience_angle} onChange={(e) => setNt({ ...nt, audience_angle: e.target.value })} />
            <Input className={inputCls} placeholder="Opening hook" value={nt.hook} onChange={(e) => setNt({ ...nt, hook: e.target.value })} />
            <Button onClick={create} disabled={busy} className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="idea-create-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Add idea</>}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ResearchBoardTab({ cid }) {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ kind: "link", value: "", note: "" });
  const load = useCallback(async () => { const { data } = await api.get(`/channels/${cid}/research`); setItems(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.value.trim()) { toast.error("Paste a URL, note or transcript"); return; }
    try { await api.post(`/channels/${cid}/research`, f); toast.success("Added"); setF({ ...f, value: "", note: "" }); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/research/${id}`); await load(); };
  if (!items) return <LoadingState />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-3">
        {items.length === 0 ? <Card><EmptyState icon={FileText} title="No research yet" description="Attach links (saved, not reviewed) or paste transcripts (evidence)." /></Card> : (
          <div className="space-y-2" data-testid="research-list">
            {items.map((it) => (
              <Card key={it.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {it.reviewed ? <BadgeCheck className="w-4 h-4 text-emerald-400" /> : <LinkIcon className="w-4 h-4 text-slate-500" />}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.reviewed ? "bg-emerald-500/10 text-emerald-300" : "bg-slate-800 text-slate-400"}`} data-testid={`research-label-${it.id}`}>{it.label}</span>
                    </div>
                    <p className="text-sm text-slate-300 mt-1 break-all">{it.value}</p>
                    {it.note && <p className="text-xs text-slate-500 mt-0.5">{it.note}</p>}
                  </div>
                  <button onClick={() => del(it.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Card>
        <h3 className="font-display font-semibold text-slate-100 mb-3">Add evidence</h3>
        <div className="space-y-2">
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
            <SelectTrigger className={inputCls} data-testid="research-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="link">Link (saved, not reviewed)</SelectItem>
              <SelectItem value="transcript">Transcript text (reviewed evidence)</SelectItem>
              <SelectItem value="note">Note</SelectItem>
            </SelectContent>
          </Select>
          {f.kind === "transcript"
            ? <Textarea className={inputCls} placeholder="Paste transcript text…" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="research-value" />
            : <Input className={inputCls} placeholder={f.kind === "link" ? "https://…" : "Note…"} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="research-value" />}
          <Input className={inputCls} placeholder="Optional note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          <Button onClick={add} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="research-add"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>
          <p className="text-xs text-slate-500">A URL alone is stored as "not reviewed". Only pasted transcript text counts as reviewed evidence.</p>
        </div>
      </Card>
    </div>
  );
}

function PlaybookTab({ cid }) {
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/channels/${cid}/playbook`).then((r) => { setContent(r.data.content || ""); setMeta(r.data); }); }, [cid]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/channels/${cid}/playbook`, { content }); setMeta(data); toast.success("Playbook saved for this channel"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <Card>
        <h3 className="font-display font-semibold text-slate-100 mb-1 flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-violet-300" /> Editorial playbook</h3>
        <p className="text-xs text-slate-500 mb-3">Channel-scoped memory. The Scriptwriter & Strategist follow these lessons/rules when generating for THIS channel only — never shared across channels.</p>
        <Textarea className={`${inputCls} min-h-[240px]`} value={content} onChange={(e) => setContent(e.target.value)} placeholder="e.g. Always mention seasonal ingredients. Never over 60s. Start with a question." data-testid="playbook-editor" />
        <div className="flex items-center gap-3 mt-3">
          <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="playbook-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save playbook</>}</Button>
          {meta?.updated_at && <span className="text-xs text-slate-600">Updated {new Date(meta.updated_at).toLocaleString()}{meta.version ? ` · v${meta.version}` : ""}</span>}
        </div>
      </Card>
      <AutolearnSection cid={cid} onApplied={() => api.get(`/channels/${cid}/playbook`).then((r) => { setContent(r.data.content || ""); setMeta(r.data); })} />
    </div>
  );
}

function RadarPanel({ cid, onRan }) {
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/channels/${cid}/radar`); setR(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/channels/${cid}/radar/run-now`);
      if (data.status === "skipped-budget") toast.error("Radar skipped — over channel budget");
      else toast.success(`Radar: ${data.created} added, ${data.skipped_duplicates} duplicate(s) skipped`);
      await load(); onRan && onRan();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!r) return null;
  const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <Card data-testid="radar-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-semibold text-slate-100 flex items-center gap-1.5"><Radar className="w-4 h-4 text-violet-300" /> Idea Radar</h3>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`} data-testid="radar-enabled-pill">{r.enabled ? "ON" : "OFF"}</span>
      </div>
      <div className="text-xs text-slate-500 space-y-1 mb-3">
        <div>Schedule: {WD[r.weekday]} {String(r.hour).padStart(2, "0")}:00 · {r.count} ideas/drop</div>
        <div>Next run: {r.enabled && r.next_run_at ? new Date(r.next_run_at).toLocaleString() : "—"}</div>
        {r.last_run && <div data-testid="radar-last-run">Last: {r.last_run.status} · {r.last_run.created} added, {r.last_run.skipped_duplicates} skipped · {new Date(r.last_run.created_at).toLocaleString()}</div>}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={run} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="radar-run-now">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Radar className="w-4 h-4 mr-1.5" /> Run now{r.estimate != null ? ` (~$${r.estimate})` : ""}</>}
        </Button>
        <button onClick={() => navigate(`/channels/${cid}`)} className="text-xs text-violet-300 underline" data-testid="radar-settings-link">Settings</button>
      </div>
    </Card>
  );
}

function AutolearnSection({ cid, onApplied }) {
  const [items, setItems] = useState([]);
  const [notEnough, setNotEnough] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/channels/${cid}/playbook/suggestions`); setItems(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const suggest = async () => {
    setBusy(true); setNotEnough(null);
    try {
      const { data } = await api.post(`/channels/${cid}/playbook/suggest`);
      if (!data.enough) { setNotEnough(data.message); toast.message(data.message); }
      else toast.success(`${data.suggestions.length} rule(s) suggested from ${data.approved_count} approved videos`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const accept = async (id) => { try { await api.post(`/playbook-suggestions/${id}/accept`); toast.success("Added to playbook"); await load(); onApplied && onApplied(); } catch (e) { toast.error(apiError(e)); } };
  const reject = async (id) => { try { await api.post(`/playbook-suggestions/${id}/reject`); await load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Card data-testid="autolearn-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-semibold text-slate-100 flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-violet-300" /> Playbook Autolearn</h3>
        <Button onClick={suggest} disabled={busy} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="autolearn-suggest">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Suggest rules</>}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Analyzes this channel's approved videos and proposes rules with evidence. Suggestions are never auto-applied.</p>
      {notEnough && <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3" data-testid="autolearn-not-enough">{notEnough}</div>}
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No pending suggestions. Click "Suggest rules" to analyze approved videos.</p>
      ) : (
        <div className="space-y-2" data-testid="autolearn-suggestions">
          {items.map((s) => (
            <div key={s.id} className="border border-slate-800 rounded-lg p-3" data-testid={`suggestion-${s.id}`}>
              <p className="text-sm text-slate-200">{s.rule}</p>
              <p className="text-xs text-slate-500 mt-1 italic">{s.evidence}</p>
              <div className="flex items-center gap-2 mt-2">
                <Button onClick={() => accept(s.id)} size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`accept-${s.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Accept</Button>
                <Button onClick={() => reject(s.id)} size="sm" variant="outline" className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800 text-xs" data-testid={`reject-${s.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CompetitorsTab({ cid }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/channels/${cid}/competitors`); setData(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const save = async (name, body) => { try { await api.put(`/channels/${cid}/competitors/${encodeURIComponent(name)}`, body); toast.success("Saved"); await load(); } catch (e) { toast.error(apiError(e)); } };
  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-4"><Users className="w-4 h-4" /> Manual-only mode — no fabricated stats. Names come from the channel profile's competitor list. Connect YouTube to fetch public data (future).</div>
      {data.competitors.length === 0 ? <Card><EmptyState title="No competitors listed" description="Add competitor channels in the channel profile first." /></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="competitors-list">
          {data.competitors.map((c) => <CompetitorCard key={c.competitor} c={c} onSave={save} />)}
        </div>
      )}
    </div>
  );
}
function CompetitorCard({ c, onSave }) {
  const [f, setF] = useState({ what_worked: c.what_worked || "", angles: c.angles || "" });
  return (
    <Card>
      <h4 className="font-medium text-slate-100 mb-2">{c.competitor}</h4>
      <Label className="text-slate-400 text-xs mb-1 block">What worked (notes)</Label>
      <Textarea className={`${inputCls} mb-2`} value={f.what_worked} onChange={(e) => setF({ ...f, what_worked: e.target.value })} />
      <Label className="text-slate-400 text-xs mb-1 block">Angles to try</Label>
      <Textarea className={`${inputCls} mb-2`} value={f.angles} onChange={(e) => setF({ ...f, angles: e.target.value })} />
      <Button onClick={() => onSave(c.competitor, f)} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700">Save notes</Button>
    </Card>
  );
}

export default function Research() {
  const { selectedChannelId, channels } = useStudio();
  const cid = selectedChannelId;
  const channel = channels.find((c) => c.id === cid);

  return (
    <div className="animate-in-up" data-testid="research-page">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Production</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Research &amp; Ideas</h1>
        <p className="text-sm text-slate-400 mt-2">Idea backlog, evidence board, editorial playbook and competitors for <span className="text-slate-200">{channel?.data?.name || "—"}</span>.</p>
      </div>
      {!cid ? (
        <Card><EmptyState icon={Lightbulb} title="No channel selected" description="Pick a channel in the top bar to manage its research." /></Card>
      ) : (
        <Tabs defaultValue="ideas">
          <TabsList className="bg-slate-900 border border-slate-800 mb-6 flex-wrap h-auto">
            {[["ideas", "Ideas"], ["research", "Research board"], ["playbook", "Playbook"], ["competitors", "Competitors"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`research-tab-${v}`} className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="ideas"><IdeasTab cid={cid} /></TabsContent>
          <TabsContent value="research"><ResearchBoardTab cid={cid} /></TabsContent>
          <TabsContent value="playbook"><PlaybookTab cid={cid} /></TabsContent>
          <TabsContent value="competitors"><CompetitorsTab cid={cid} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
